import React from 'react';
import Icon from '../../../common/Icon/Icon';
import { formatDate, formatDateTime, formatCurrency } from '../../../../utils';
import { CHARGE_TYPE_LABELS, STATUS_LABELS, STATUS_ICON, normalizeStatus } from '../paymentDetails.constants';
import styles from './PayloadView.module.css';

interface PayPalPayloadViewProps {
  payload: Record<string, unknown>;
  currentStatus?: string;
  currentCurrency?: 'BRL' | 'USD';
}

const PayPalPayloadView: React.FC<PayPalPayloadViewProps> = ({ payload, currentStatus, currentCurrency }) => {
  const payment = (payload.payment ?? payload) as Record<string, unknown>;
  const status = normalizeStatus(currentStatus ?? (payment.status as string) ?? '');
  const statusLabel = STATUS_LABELS[status] ?? (payment.status as string) ?? '—';
  const statusIcon = STATUS_ICON[status] ?? { name: 'help', color: 'var(--color-solid-info)' };
  const currency = ((payment.currency as string)
    ?? currentCurrency
    ?? 'BRL').toUpperCase() as 'BRL' | 'USD';
  const chargeType = typeof payment.charge_type === 'string' ? payment.charge_type : null;
  const paypalStatus = typeof payment.paypalStatus === 'string' ? payment.paypalStatus : null;

  return (
    <div className={styles.container}>
      <Row label="ID da cobrança" value={payment.id as string} />
      {!!payment.description && <Row label="Descrição" value={payment.description as string} />}
      <Row label="Moeda" value={currency} />
      <Row label="Valor" value={formatCurrency(Number(payment.value ?? 0), currency)} />
      <Row
        label="Status"
        element={(
          <span className={styles.status}>
            <Icon name={statusIcon.name} size={16} filled color={statusIcon.color} />
            {statusLabel}
          </span>
        )}
      />
      {paypalStatus && paypalStatus !== statusLabel && (
        <Row label="Status PayPal" value={paypalStatus} />
      )}
      {chargeType && (
        <Row label="Tipo" value={CHARGE_TYPE_LABELS[chargeType] ?? chargeType} />
      )}
      {!!payment.invoiceNumber && <Row label="Número da fatura" value={payment.invoiceNumber as string} />}
      {!!payment.external_subscription_id && (
        <Row label="Assinatura PayPal" value={payment.external_subscription_id as string} />
      )}
      {!!payment.recipientEmail && <Row label="E-mail da aluna" value={payment.recipientEmail as string} />}
      {!!payment.recipientName && <Row label="Nome da aluna" value={payment.recipientName as string} />}
      {!!payment.dateCreated && <Row label="Data de criação" value={formatDate(payment.dateCreated as string)} />}
      {!!payment.dueDate && <Row label="Vencimento" value={formatDate(payment.dueDate as string)} />}
      {!!payment.paymentDate && <Row label="Data de pagamento" value={formatDateTime(payment.paymentDate as string)} />}

      {!!payment.invoiceUrl && (
        <Row
          label="Link de pagamento"
          element={(
            <a href={payment.invoiceUrl as string} target="_blank" rel="noopener noreferrer" className={styles.link}>
              <Icon name="open_in_new" size={14} />
              Abrir no PayPal
            </a>
          )}
        />
      )}
    </div>
  );
};

const Row: React.FC<{ label: string; value?: string; element?: React.ReactNode }> = ({ label, value, element }) => (
  <div className={styles.row}>
    <span className={styles.label}>{label}</span>
    {element ?? <span className={styles.value}>{value ?? '—'}</span>}
  </div>
);

export default PayPalPayloadView;
